import { useEffect, useState } from 'react';
import { Button } from '../../ui';
import { fetchAccess, type CourseAccess } from '../../lib/access';
import { fetchOffer } from '../../lib/offer';
import type { Plan } from '../../lib/checkout';
import { calendarDate } from '../../lib/format';
import { sound } from '../../lib/sound';
import { useLocale, useStrings } from '../../state/useLocale';
import type { AccountApi } from '../../state/useAccount';

/**
 * The account's period of the course, on the ledger (strings: `home.term`).
 *
 * The course is a period at a time now, three months or one
 * (worker/migrations/0032, worker/stripe.ts `Plan`), and the offer block
 * draws nothing for an account that owns everything. Without
 * this, the first a learner heard of their access ending would be the levels
 * locking. So there are three sentences, and at most one of them shows:
 *
 * - most of the period, a quiet line saying when it ends;
 * - in the last fourteen days, when the Worker opens the checkout again,
 *   that line with a renew button, and the reason renewing early is safe;
 * - after it ends, a line over the offer, which is back by then because the
 *   levels have relocked, so the price reads as "three more months".
 *
 * Nothing for an account that never had the course: the offer below is the
 * whole story for them.
 *
 * `version` is anything that changes when ownership might have: the missions
 * list is replaced whenever the catalogue gains or loses the paid half
 * (state/usePaid.ts), so passing it re-reads the year after a purchase lands.
 */
export function TermNote({
  account,
  version,
  onUnlock,
}: {
  account: AccountApi;
  version: unknown;
  onUnlock(plan?: Plan): void;
}) {
  const s = useStrings().home.term;
  const { locale } = useLocale();
  const [access, setAccess] = useState<CourseAccess | null>(null);
  // Whether a month is sold here at all (lib/offer.ts). The same one request
  // the offer block makes, shared, so asking costs nothing more.
  const [monthSold, setMonthSold] = useState(false);
  const userId = account.user?.id ?? null;

  useEffect(() => {
    let live = true;
    void fetchOffer().then((offer) => {
      if (live) setMonthSold(offer?.month != null && offer.payable);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setAccess(null);
      return;
    }
    let live = true;
    void fetchAccess().then((next) => {
      if (live) setAccess(next);
    });
    return () => {
      live = false;
    };
  }, [userId, version]);

  if (!access || access.until === null) return null;
  const date = calendarDate(new Date(access.until * 1000), locale);

  if (!access.owned) {
    return (
      <section className="term-note" role="status">
        <p className="note">{s.ended(date)}</p>
      </section>
    );
  }

  return (
    <section className="term-note">
      <p className="note">{access.renewable ? s.renewSoon(date) : s.until(date)}</p>
      {/* Both lengths, whichever this account bought last: renewing is when
          somebody on a month decides whether to take three, and the other
          way round. The three months stay the solid one. */}
      {access.renewable && (
        <>
          <Button
            variant="solid"
            onClick={() => {
              sound.click();
              onUnlock();
            }}
          >
            {s.renew}
          </Button>
          {monthSold && (
            <Button
              variant="quiet"
              onClick={() => {
                sound.click();
                onUnlock('month');
              }}
            >
              {s.renewMonth}
            </Button>
          )}
        </>
      )}
    </section>
  );
}
